import { useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";

// Allow-list of safe intent/date-like query keys we will forward.
// Explicitly excludes any host/cfx/payment/signature/session parameters
// that could otherwise be smuggled through a redirect.
const SAFE_KEYS = new Set([
  "intent",
  "date",
  "start",
  "end",
  "checkin",
  "check_in",
  "checkout",
  "check_out",
  "start_date",
  "end_date",
  "nights",
  "guests",
  "adults",
  "children",
]);

const SAFE_VALUE_RE = /^[A-Za-z0-9_\-:.]{1,64}$/;

export function buildSafeQuery(search: string, extra: Record<string, string> = {}): string {
  const src = new URLSearchParams(search);
  const out = new URLSearchParams();
  src.forEach((value, key) => {
    const k = key.toLowerCase();
    if (!SAFE_KEYS.has(k)) return;
    if (!SAFE_VALUE_RE.test(value)) return;
    if (!out.has(k)) out.set(k, value);
  });
  for (const [k, v] of Object.entries(extra)) {
    if (!SAFE_KEYS.has(k)) continue;
    if (!SAFE_VALUE_RE.test(v)) continue;
    out.set(k, v);
  }
  const s = out.toString();
  return s ? `?${s}` : "";
}

/**
 * Local Island alias -> /book-now. Overnight variants pass ?intent=overnight.
 * Never renders a raw Checkfront/Droplet widget.
 */
export function LocalBookingRedirect({ intent }: { intent?: "overnight" }) {
  const location = useLocation();
  const to = useMemo(() => {
    const extra = intent ? { intent } : {};
    return `/book-now${buildSafeQuery(location.search, extra)}`;
  }, [location.search, intent]);
  return <Navigate to={to} replace />;
}

/**
 * Sister-property alias -> official guarded booking page on that property's
 * own domain. Client-side hard redirect via window.location.replace.
 * Never mounts the raw Checkfront widget.
 */
export function ExternalBookingRedirect({ url }: { url: string }) {
  const location = useLocation();
  const target = useMemo(() => `${url}${buildSafeQuery(location.search)}`, [url, location.search]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        window.location.replace(target);
      } catch {
        window.location.href = target;
      }
    }
  }, [target]);
  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="font-display text-2xl font-semibold">Redirecting to secure booking…</h1>
        <p className="text-muted-foreground">
          If you are not redirected automatically, use the link below.
        </p>
        <a
          className="inline-block underline text-primary font-medium"
          href={target}
          rel="noopener noreferrer"
        >
          Continue to booking
        </a>
      </div>
    </main>
  );
}

// Canonical external targets for the five modern/protected sister properties.
// NOTE: Guadalupe is intentionally excluded and continues to use legacy pages.
export const EXTERNAL_BOOKING_TARGETS = {
  riverRanch: "https://sonsriverranch.com/book",
  island: "https://sonsisland.com/book-now",
  geronimo: "https://sonsgeronimo.com/book-now",
  rioCibolo: "https://sonsriocibolo.com/book-now",
  blueRiver: "https://bluerivercamp.com/book-now",
} as const;
